import { Queue } from "./queue";

/**
 * Resolvable promise.
 */
export class Condition<T = void> {
  resolve!: (value: T) => void;

  reject!: (e: any) => void;

  readonly result = new Promise<T>((resolve, reject) => {
    this.resolve = resolve;
    this.reject = reject;
  });

  use(promise: Promise<T>) {
    promise.then(this.resolve, this.reject);
  }
}

export interface AsyncWriteable<T> {
  end(): Promise<void>;
  throw(error: any): Promise<void>;
  next(value: T): Promise<void>;
}

/**
 * Creates a pipe that buffers one item at a time, and exposes them as an async iterator.
 *
 * This uses a fixed (1) buffer, this supports backpressure.
 * To buffer multiple items, use bufferedPipe().
 */
export function asyncPipe<T>(): [AsyncWriteable<T>, AsyncIterableIterator<T>] {
  const end = Symbol();

  let next = new Condition<T | typeof end>();
  let writeReady: Condition<void> | undefined;

  const write: AsyncWriteable<T> = {
    end() {
      if (writeReady) {
        throw new Error("Pipe already has item");
      }
      writeReady = new Condition<void>();
      next.resolve(end);
      return writeReady.result;
    },
    throw(error) {
      if (writeReady) {
        throw new Error("Pipe already has item");
      }
      writeReady = new Condition<void>();
      next.reject(error);
      return writeReady.result;
    },
    next(item) {
      if (writeReady) {
        throw new Error("Pipe already has item");
      }
      writeReady = new Condition<void>();
      next.resolve(item);
      return writeReady.result;
    },
  };

  const read = (async function* () {
    while (true) {
      const result = await next.result;
      try {
        if (result === end) {
          break;
        }
        yield result;
        next = new Condition();
      } finally {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        writeReady!.resolve();
        writeReady = undefined;
      }
    }
  })();

  return [write, read];
}

export interface Writeable<T> {
  end(): void;
  throw(error: any): void;
  next(value: T): void;
}

/**
 * Creates a pipe that buffers writes, and exposes them as an async iterator.
 */
export function bufferedPipe<T>(): [Writeable<T>, AsyncIterableIterator<T>] {
  const end = Symbol();
  const queue = new Queue<Promise<T> | typeof end>();

  let condition: Condition<void> | undefined;
  const enqueue = (promise: Promise<T> | typeof end) => {
    queue.push(promise);
    condition?.resolve();
  };
  const write: Writeable<T> = {
    end() {
      enqueue(end);
    },
    throw(error) {
      enqueue(Promise.reject(error));
    },
    next(item) {
      enqueue(Promise.resolve(item));
    },
  };

  const read = (async function* () {
    while (true) {
      if (queue.empty()) {
        condition = new Condition();
        await condition.result;
        condition = undefined;
      }
      const promise = queue.pop();
      if (promise === end) {
        break;
      }
      yield promise;
    }
  })();

  return [write, read];
}
